import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import TwitterIcon from "@mui/icons-material/Twitter";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

import Container from "../../components/Container";

const COLORS = {
  primary: "#2DD736",
  dark: "#053418",
  cta: "#0A5529",
  grey: "#375D46",
  greenGray: "#E5EAD9",
  lighter: "#D5F4D7",
  white: "#FFFFFF",
};

const SOCIAL_SUBSCRIBERS = [
  { id: 1, restaurant: "The Curry House", owner: "Rahul Sharma", plan: "Pro", status: "active", subscribedDate: "01 Mar 2026", features: ["instagram", "facebook"], posts: 142, followers: "12.4K" },
  { id: 2, restaurant: "Bistro 21", owner: "Emma Watson", plan: "Enterprise", status: "active", subscribedDate: "15 Jan 2026", features: ["instagram", "facebook", "twitter"], posts: 89, followers: "8.1K" },
  { id: 3, restaurant: "Spice Garden", owner: "Ali Hassan", plan: "Pro", status: "active", subscribedDate: "20 Feb 2026", features: ["instagram"], posts: 64, followers: "5.3K" },
  { id: 4, restaurant: "Ocean Bites", owner: "Sarah Lee", plan: "Basic", status: "inactive", subscribedDate: "10 Apr 2026", features: ["facebook"], posts: 12, followers: "980" },
  { id: 5, restaurant: "The Green Fork", owner: "David Chen", plan: "Enterprise", status: "active", subscribedDate: "05 Dec 2025", features: ["instagram", "facebook", "twitter"], posts: 215, followers: "21.7K" },
  { id: 6, restaurant: "Golden Wok", owner: "Lin Zhang", plan: "Pro", status: "active", subscribedDate: "18 Mar 2026", features: ["instagram", "facebook"], posts: 98, followers: "7.2K" },
  { id: 7, restaurant: "Pizza Piazza", owner: "Marco Rossi", plan: "Basic", status: "trial", subscribedDate: "12 Jun 2026", features: ["instagram"], posts: 5, followers: "420" },
  { id: 8, restaurant: "Sushi Spot", owner: "Yuki Tanaka", plan: "Pro", status: "active", subscribedDate: "22 Feb 2026", features: ["instagram", "twitter"], posts: 176, followers: "14.9K" },
  { id: 9, restaurant: "Casa Blanca", owner: "Sofia Garcia", plan: "Enterprise", status: "active", subscribedDate: "08 Jan 2026", features: ["instagram", "facebook", "twitter"], posts: 301, followers: "28.4K" },
  { id: 10, restaurant: "The Noodle Bar", owner: "Priya Patel", plan: "Basic", status: "inactive", subscribedDate: "30 May 2026", features: ["facebook"], posts: 8, followers: "650" },
];

const STATUS_MAP = {
  active: { bg: "#D5F4D7", color: "#0A5529", label: "Active" },
  inactive: { bg: "#F3F4F6", color: "#6B7280", label: "Inactive" },
  trial: { bg: "#E0F2FE", color: "#0369A1", label: "Trial" },
};

const PLAN_MAP = {
  Basic: { bg: "#F3F4F6", color: "#374151" },
  Pro: { bg: "#FEF3C7", color: "#92400E" },
  Enterprise: { bg: "#D5F4D7", color: "#0A5529" },
};

const SocialIcon = ({ type }) => {
  const props = { sx: { fontSize: 16 } };
  switch (type) {
    case "instagram": return <InstagramIcon {...props} sx={{ ...props.sx, color: "#E1306C" }} />;
    case "facebook": return <FacebookOutlinedIcon {...props} sx={{ ...props.sx, color: "#1877F2" }} />;
    case "twitter": return <TwitterIcon {...props} sx={{ ...props.sx, color: "#1DA1F2" }} />;
    default: return null;
  }
};

const SummaryCard = ({ label, value, sub, color }) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9" }}>
    <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 12.5, color: COLORS.grey, mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontFamily: "GilroyBold", fontSize: 28, color: color || COLORS.dark, lineHeight: 1 }}>{value}</Typography>
    {sub && <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 11.5, color: "#9CA3AF", mt: 0.5 }}>{sub}</Typography>}
  </Paper>
);

const Socials = (props) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");

  const filtered = useMemo(() => {
    return SOCIAL_SUBSCRIBERS.filter((r) => {
      const matchSearch = !search || r.restaurant.toLowerCase().includes(search.toLowerCase()) || r.owner.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || r.status === statusFilter;
      const matchPlan = planFilter === "all" || r.plan === planFilter;
      const matchPlatform = platformFilter === "all" || r.features.includes(platformFilter);
      return matchSearch && matchStatus && matchPlan && matchPlatform;
    });
  }, [search, statusFilter, planFilter, platformFilter]);

  const activeCount = SOCIAL_SUBSCRIBERS.filter((r) => r.status === "active").length;
  const totalFollowers = "99.3K";

  return (
    <Container {...props} page="socials" onSearch={() => {}}>
      <Box sx={{ maxWidth: 1400 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <ShareOutlinedIcon sx={{ fontSize: 22, color: COLORS.cta }} />
            <Typography sx={{ fontFamily: "GilroyBold", fontSize: 26, color: COLORS.dark }}>
              Socials
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 13.5, color: COLORS.grey }}>
            Restaurants subscribed to the Foodo Social feature.
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Total Subscribers" value={SOCIAL_SUBSCRIBERS.length} sub="All plans" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Active" value={activeCount} sub="Currently active" color={COLORS.cta} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Total Followers" value={totalFollowers} sub="Across all platforms" color={COLORS.cta} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Total Posts" value="1,110" sub="All time" />
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "12px",
            backgroundColor: COLORS.white,
            border: "1px solid #E5EAD9",
            mb: 2,
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search restaurant or owner..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  sx: { fontFamily: "GilroyRegular", fontSize: 13, borderRadius: "8px" },
                }}
                sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}
              />
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <Select
                fullWidth
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{ fontFamily: "GilroySemiBold", fontSize: 13, borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <Select
                fullWidth
                size="small"
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                displayEmpty
                sx={{ fontFamily: "GilroySemiBold", fontSize: 13, borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}
              >
                <MenuItem value="all">All Plans</MenuItem>
                <MenuItem value="Basic">Basic</MenuItem>
                <MenuItem value="Pro">Pro</MenuItem>
                <MenuItem value="Enterprise">Enterprise</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <Select
                fullWidth
                size="small"
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                displayEmpty
                sx={{ fontFamily: "GilroySemiBold", fontSize: 13, borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}
              >
                <MenuItem value="all">All Platforms</MenuItem>
                <MenuItem value="instagram">Instagram</MenuItem>
                <MenuItem value="facebook">Facebook</MenuItem>
                <MenuItem value="twitter">Twitter</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} sm="auto">
              <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: 13, color: COLORS.grey }}>
                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            backgroundColor: COLORS.white,
            border: "1px solid #E5EAD9",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
                  {["Restaurant", "Owner", "Plan", "Platforms", "Posts", "Followers", "Subscribed", "Status"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontFamily: "GilroySemiBold",
                        fontSize: 12,
                        color: COLORS.grey,
                        borderBottom: "1px solid #E5EAD9",
                        py: 1.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: COLORS.grey, fontFamily: "GilroyRegular" }}>
                      No results match your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => {
                    const s = STATUS_MAP[row.status];
                    const p = PLAN_MAP[row.plan];
                    return (
                      <TableRow
                        key={row.id}
                        sx={{ "&:hover": { backgroundColor: "#FAFDF9" }, "&:last-child td": { border: 0 } }}
                      >
                        <TableCell sx={{ fontFamily: "GilroySemiBold", fontSize: 13, color: COLORS.dark, borderBottom: "1px solid #F3F4F6" }}>
                          {row.restaurant}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "GilroyRegular", fontSize: 13, color: "#6B7280", borderBottom: "1px solid #F3F4F6" }}>
                          {row.owner}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <Chip label={row.plan} size="small" sx={{ fontFamily: "GilroySemiBold", fontSize: 11, backgroundColor: p.bg, color: p.color, height: 22 }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            {row.features.map((f) => <SocialIcon key={f} type={f} />)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "GilroySemiBold", fontSize: 13, color: COLORS.dark, borderBottom: "1px solid #F3F4F6" }}>
                          {row.posts}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "GilroySemiBold", fontSize: 13, color: COLORS.cta, borderBottom: "1px solid #F3F4F6" }}>
                          {row.followers}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "GilroyRegular", fontSize: 12.5, color: "#6B7280", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>
                          {row.subscribedDate}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <Chip label={s.label} size="small" sx={{ fontFamily: "GilroySemiBold", fontSize: 11, backgroundColor: s.bg, color: s.color, height: 22 }} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
};

export default Socials;
